import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import ajaxService from "../../services/ajax-service";
import dayjs from "dayjs";
import {
  Card,
  Spin,
  Alert,
  Button,
  Avatar,
  Tabs,
  List,
  Tag,
  Empty,
  Descriptions,
  Row,
  Col,
  Statistic,
  Form,
  Input,
  notification,
} from "antd";
import {
  UserOutlined,
  TeamOutlined,
  HeartOutlined,
  QuestionCircleOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
  CrownOutlined,
  CalendarOutlined,
  LockOutlined,
} from "@ant-design/icons";

const { TabPane } = Tabs;

/** ---------- Small Presentational Pieces ---------- **/

const UserInfo = ({ user }) => (
  <Card 
    bordered 
    style={{
      marginBottom: 'var(--spacing-lg)',
      borderRadius: 'var(--radius-2xl)',
      boxShadow: 'var(--shadow-card)',
      background: 'var(--dark-bg-card)',
      border: '1px solid rgba(254, 2, 125, 0.2)',
    }}
  >
    <Descriptions bordered column={1} size="middle">
      <Descriptions.Item label="Name"><span style={{ color: 'var(--text-white)' }}>{user?.name || "N/A"}</span></Descriptions.Item>
      <Descriptions.Item label="Email"><span style={{ color: 'var(--text-white)' }}>{user?.email || "N/A"}</span></Descriptions.Item>
      <Descriptions.Item label="Phone"><span style={{ color: 'var(--text-white)' }}>{user?.phone || "N/A"}</span></Descriptions.Item>
      {/* <Descriptions.Item label="Username"><span style={{ color: 'var(--text-white)' }}>{user?.user_name || "N/A"}</span></Descriptions.Item> */}
      <Descriptions.Item label="Gender"><span style={{ color: 'var(--text-white)' }}>{user?.gender || "N/A"}</span></Descriptions.Item>
      <Descriptions.Item label="Date of Birth">
        <span style={{ color: 'var(--text-white)' }}>
        {user?.dob ? dayjs(user.dob).format("MMMM D, YYYY") : "N/A"}
        </span>
      </Descriptions.Item>
      <Descriptions.Item label="Age"><span style={{ color: 'var(--text-white)' }}>{user?.age ?? "N/A"}</span></Descriptions.Item>
    </Descriptions>
  </Card>
);

const RolesList = ({ roles = [] }) =>
  roles.length ? (
    <List
      dataSource={roles}
      renderItem={(role) => (
        <Tag
          color="blue"
          icon={<CheckCircleOutlined />}
          key={role?.id || role?.name}
        >
          {role?.name}
        </Tag>
      )}
    />
  ) : (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No roles" />
  );

const InterestsList = ({ interestUsers = [] }) =>
  interestUsers.length ? (
    <List
      grid={{ gutter: 12, column: 2 }}
      dataSource={interestUsers}
      renderItem={(iu, idx) => (
        <List.Item key={`${iu?.interest?.id}-${iu?.interest_type?.id}-${idx}`}>
          <Tag color="geekblue" icon={<HeartOutlined />}>
            {iu?.interest?.name || "Unknown"}{" "}
            {iu?.interest_type?.name ? `(${iu.interest_type.name})` : ""}
          </Tag>
        </List.Item>
      )}
    />
  ) : (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No interests" />
  );

const LifestyleAnswersList = ({ answers = [] }) =>
  answers.length ? (
    <List
      itemLayout="vertical"
      dataSource={answers}
      renderItem={(ans) => (
        <Card
          size="small"
          style={{
            marginBottom: 'var(--spacing-sm)',
            borderRadius: 'var(--radius-xl)',
            boxShadow: 'var(--shadow-card)',
            background: 'var(--dark-bg-card)',
            border: '1px solid rgba(254, 2, 125, 0.2)',
          }}
          title={<span style={{ color: 'var(--text-white)' }}>{ans?.lifestyle?.question || `Question ${ans?.question_id}`}</span>}
        >
          <p style={{ fontWeight: 500, color: 'var(--text-white)' }}>{ans?.answer ?? "N/A"}</p>
        </Card>
      )}
    />
  ) : (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No lifestyle answers" />
  );

const QuestionAnswersList = ({ answers = [] }) =>
  answers.length ? (
    <List
      itemLayout="vertical"
      dataSource={answers}
      renderItem={(ans) => (
        <Card
          size="small"
          style={{
            marginBottom: 'var(--spacing-sm)',
            borderRadius: 'var(--radius-xl)',
            boxShadow: 'var(--shadow-card)',
            background: 'var(--dark-bg-card)',
            border: '1px solid rgba(254, 2, 125, 0.2)',
          }}
          title={<span style={{ color: 'var(--text-white)' }}>{ans?.question?.question || `Question ${ans?.question_id}`}</span>}
        >
          <p style={{ fontWeight: 500, color: 'var(--text-white)' }}>{ans?.answer ?? "N/A"}</p>
        </Card>
      )}
    />
  ) : (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No custom answers" />
  );

/** ---------- Main Component ---------- **/

const UserDetails = () => {
  const id = 1;
  const history = useHistory(); // v5

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [passwordForm] = Form.useForm();
  const [passwordLoading, setPasswordLoading] = useState(false);

  const fetchUser = useCallback(async (userId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ajaxService.get(`/users/${userId}`);
      if (response?.success) {
        setUser(response.data || {});
      } else {
        setError(response?.message || "Failed to fetch user data.");
        setUser(null);
      }
    } catch (err) {
      console.error("Error fetching user:", err);
      setError("Error fetching user data.");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchUser(id);
  }, [id, fetchUser]);

  // Listen for sidebar collapse state
  useEffect(() => {
    const handleSidebarCollapse = (event) => {
      setSidebarCollapsed(event.detail.collapsed);
    };

    window.addEventListener('sidebarCollapse', handleSidebarCollapse);
    return () => window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
  }, []);

  const avatarSrc = useMemo(() => {
    const src = user?.profile_image;
    return src && String(src).trim() ? src : undefined;
  }, [user?.profile_image]);

  const onPasswordResetFinish = useCallback(async (values) => {
    if (values.new_password !== values.confirm_password) {
      notification.error({ message: "New password and confirm password do not match." });
      return;
    }
    setPasswordLoading(true);
    try {
      const response = await ajaxService.post("/change-password", {
        current_password: values.current_password,
        new_password: values.new_password,
        new_password_confirmation: values.confirm_password,
      });
      if (response?.success) {
        notification.success({
          message: "Password updated",
          description: "Your password has been changed successfully. Please log in again.",
          icon: <CheckCircleOutlined style={{ color: "var(--success-green)" }} />,
          duration: 3,
        });
        passwordForm.resetFields();
        // Logout user and redirect to login
        localStorage.removeItem("token");
        localStorage.removeItem("name");
        localStorage.removeItem("role");
        localStorage.removeItem("courtID");
        setTimeout(() => {
          window.location.href = "/login";
        }, 1500);
      } else {
        notification.error({
          message: response?.message || "Failed to update password",
          description: response?.data?.message || response?.errors?.current_password?.[0] || "",
        });
      }
    } catch (err) {
      console.error("Password reset error:", err);
      const msg = err?.response?.data?.message || err?.message || "An unexpected error occurred.";
      notification.error({ message: "Error", description: msg });
    } finally {
      setPasswordLoading(false);
    }
  }, [user?.id, passwordForm]);

  if (error) {
    return (
      <div className="page-container" style={{ padding: 'var(--spacing-xl)', display: 'flex', justifyContent: 'center' }}>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginTop: 'var(--spacing-2xl)', maxWidth: '600px', width: '100%' }}
        />
      </div>
    );
  }

  if (!user && loading) {
    return (
      <div className="page-container" style={{ padding: 'var(--spacing-xl)', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!user && !loading) {
    return (
      <div className="page-container" style={{ padding: 'var(--spacing-xl)', display: 'flex', justifyContent: 'center' }}>
        <Alert
          message="No user found"
          type="warning"
          showIcon
          style={{ marginTop: 'var(--spacing-2xl)', maxWidth: '600px', width: '100%' }}
        />
      </div>
    );
  }

  return (
    <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Header Section */}
      <div className="module-header">
        <h1 className="module-title">Profile Details</h1>
        <p className="module-subtitle">View and manage your profile information</p>
      </div>


      <Spin spinning={loading}>
        <Card
          style={{
            backgroundColor: 'var(--dark-bg-card)',
            border: '1px solid rgba(254, 2, 125, 0.2)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-card)',
          }}
          title={<span style={{ color: 'var(--text-white)', fontSize: '1.5rem', fontFamily: 'Poppins-Bold' }}></span>}
          extra={
            <>
            <Link to={`/update-profile/${user?.id}`}>
                    <Button 
                        type='primary' 
                        style={{ 
                            background: 'var(--accent-purple)', 
                            borderColor: 'var(--accent-purple)',
                            color: 'white', 
                            fontSize: '14px', 
                            marginRight:'5px',
                            borderRadius: 'var(--radius-md)'
                        }}
                    >
                        Edit
                    </Button>
                    </Link>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => history.goBack()}
              style={{
                backgroundColor: 'var(--primary-purple)',
                borderColor: 'var(--primary-purple)',
                color: 'white',
              }}
            >
              Back
            </Button>
            </>
          }
        >
          {/* Top section with avatar + info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xl)' }}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: 'var(--spacing-xl)', flexWrap: 'wrap' }}>
            {/* Avatar & basic info */}
            <Card
                style={{
                  flex: 1,
                  minWidth: '200px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 'var(--radius-2xl)',
                  boxShadow: 'var(--shadow-card)',
                  background: 'var(--dark-bg-card)',
                  border: '1px solid rgba(254, 2, 125, 0.2)',
                }}
              bordered
            >
              <Avatar
                size={120}
                icon={<UserOutlined />}
                src={avatarSrc}
                  style={{ 
                    marginBottom: 'var(--spacing-lg)',
                    border: '2px solid var(--primary-purple)',
                  }}
              />
                <h2 style={{ fontSize: '1.125rem', fontFamily: 'Poppins-Bold', color: 'var(--text-white)', marginBottom: 'var(--spacing-xs)' }}>{user?.name}</h2>
                <p style={{ color: 'var(--text-gray)', marginBottom: 'var(--spacing-xs)' }}>{user?.email}</p>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-gray-light)' }}>
                Member since {dayjs(user?.created_at).format("MMM YYYY")}
              </p>
            </Card>

            {/* User Info */}
              <div style={{ flex: 1, minWidth: '300px' }}>
              <UserInfo user={user} />
              </div>
            </div>

            {/* Password Reset - Grid */}
            <Card
              title={
                <span style={{ color: 'var(--text-white)', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <LockOutlined /> Change Password
                </span>
              }
              style={{
                marginTop: 'var(--spacing-lg)',
                borderRadius: 'var(--radius-2xl)',
                boxShadow: 'var(--shadow-card)',
                background: 'var(--dark-bg-card)',
                border: '1px solid rgba(254, 2, 125, 0.2)',
              }}
            >
              <Form
                form={passwordForm}
                name="password-reset"
                layout="vertical"
                onFinish={onPasswordResetFinish}
              >
                <Row gutter={[24, 0]}>
                  <Col xs={24} sm={24} md={8} lg={8}>
                    <Form.Item
                      name="current_password"
                      label={<span style={{ color: 'var(--text-white)' }}>Current Password</span>}
                      rules={[{ required: true, message: 'Please enter your current password.' }]}
                    >
                      <Input.Password
                        prefix={<LockOutlined style={{ color: 'var(--text-gray-light)' }} />}
                        placeholder="Current password"
                        size="large"
                        style={{
                          borderRadius: 'var(--radius-md)',
                          background: 'var(--dark-bg-card)',
                          border: '1px solid rgba(254, 2, 125, 0.2)',
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8} lg={8}>
                    <Form.Item
                      name="new_password"
                      label={<span style={{ color: 'var(--text-white)' }}>New Password</span>}
                      rules={[
                        { required: true, message: 'Please enter your new password.' },
                        { min: 8, message: 'Password must be at least 8 characters.' },
                      ]}
                    >
                      <Input.Password
                        prefix={<LockOutlined style={{ color: 'var(--text-gray-light)' }} />}
                        placeholder="New password"
                        size="large"
                        style={{
                          borderRadius: 'var(--radius-md)',
                          background: 'var(--dark-bg-card)',
                          border: '1px solid rgba(254, 2, 125, 0.2)',
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8} lg={8}>
                    <Form.Item
                      name="confirm_password"
                      label={<span style={{ color: 'var(--text-white)' }}>Confirm New Password</span>}
                      dependencies={['new_password']}
                      rules={[
                        { required: true, message: 'Please confirm your new password.' },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('new_password') === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('Passwords do not match.'));
                          },
                        }),
                      ]}
                    >
                      <Input.Password
                        prefix={<LockOutlined style={{ color: 'var(--text-gray-light)' }} />}
                        placeholder="Confirm new password"
                        size="large"
                        style={{
                          borderRadius: 'var(--radius-md)',
                          background: 'var(--dark-bg-card)',
                          border: '1px solid rgba(254, 2, 125, 0.2)',
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={passwordLoading}
                    size="large"
                    style={{
                      background: 'var(--gradient-primary)',
                      border: 'none',
                      borderRadius: 'var(--radius-md)',
                      fontWeight: 600,
                    }}
                  >
                    Update Password
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </div>
        </Card>
      </Spin>
    </div>
  );
};

export default UserDetails;
